import { MAX_INTEGER } from "@pdfking/math"

import type { PageEntry } from "#internal"
import type { SortFunction } from "#internal"

import { PathKey } from "../../common/layout/PathKey"

const group_by_child_reference = (pages: PageEntry[]): Array<PageEntry[]> => {
  const order: string[] = []
  const groups = new Map<string, PageEntry[]>()
  for (const page of pages) {
    const key = PathKey(page.group)
    const group = groups.get(key)
    if (group) group.push(page)
    else {
      groups.set(key, [page])
      order.push(key)
    }
  }
  return order.map(key => groups.get(key)!)
}

const ontop_sorting = (limit_to: number): SortFunction =>
  function* (pages) {
    let tail: PageEntry[] = []

    const empty_tail = function* () {
      yield* tail
      tail = []
    }

    for (const inner_pages of group_by_child_reference([...pages])) {
      const count = inner_pages.length
      const first_page = inner_pages[0]!
      const is_book = count > 2 && Boolean(first_page.meta.page_total)

      if (is_book) {
        if (count % 4 !== 0) {
          throw new Error(`book signature page count ${count} is not a multiple of 4`)
        }

        if (count + 2 * tail.length > limit_to) {
          yield* empty_tail()
          if (count > limit_to) {
            throw new Error(
              `a signature with ${count} pages is larger than maximum limit of ${limit_to} pages`
            )
          }
        }

        yield* inner_pages.slice(0, count / 2)
        for (let i = count - 1; i >= count / 2; i--) {
          tail.unshift(inner_pages[i]!)
        }
      } else {
        yield* empty_tail()
        yield* inner_pages
      }
    }

    yield* tail
  }

export const MakeOnTopSortingFunction = (limit_to?: number): SortFunction =>
  ontop_sorting(limit_to ?? MAX_INTEGER)
